import { useHistoryLogs } from "../../../../hooks/useHistoryLogs";
import CustomLoader from "../../../../components/CustomLoader/CustomLoader";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useTranslation } from "../../../../hooks/useTranslation";
import './HistoryLogsTable.scss';

const HistoryLogsTable = ({ equipment, id, hiddenKeys, toggleKeyVisibility, showAllKeys }) => {
    const {t} = useTranslation();
    const { data, isLoading, isError, error } = useHistoryLogs(equipment, id);
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Handle error toast
    useEffect(() => {
        if (isError && error) {
            toast.error(error.message || t('historyLogTable.failedToLoad'));
        }
    }, [isError, error]);

    // Toggle row expansion
    const toggleRow = (rowId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(rowId)) {
            newExpandedRows.delete(rowId);
        } else {
            newExpandedRows.add(rowId);
        }
        setExpandedRows(newExpandedRows);
    };

    // Get all unique keys from all data items
    const getAllKeys = () => {
        const keys = new Set();
        data?.forEach(item => {
            if (item.data && typeof item.data === 'object') {
                Object.keys(item.data).forEach(key => keys.add(key));
            }
        });
        return Array.from(keys).sort();
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return t('historyLogTable.notAvailable');
        try {
            return new Date(dateString).toLocaleString();
        } catch (error) {
            return dateString;
        }
    };

    // Get action badge color
    const getActionBadgeClass = (action) => {
        switch (action?.toLowerCase()) {
            case 'created':
                return 'action-badge--created';
            case 'updated':
                return 'action-badge--updated';
            case 'deleted':
                return 'action-badge--deleted';
            default:
                return 'action-badge--default';
        }
    };

    if (isLoading) {
        return (
            <div className="history-logs-table__loader">
                <CustomLoader width={40} />
                <p>{t('historyLogTable.loading')}</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="history-logs-table__error">
                <div className="history-logs-table__error-icon">⚠️</div>
                <h3>{t('historyLogTable.errorLoading')}</h3>
                <p>{error?.message || t('historyLogTable.failedToLoad')}</p>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="history-logs-table__empty">
                <div className="history-logs-table__empty-icon">📋</div>
                <h3>{t('historyLogTable.noDataFound')}</h3>
                <p>{t('historyLogTable.noDataMessage')}</p>
            </div>
        );
    }

    const allKeys = getAllKeys();
    const hasHiddenKeys = hiddenKeys.size > 0;

    return (
        <div className="history-logs-table">
            <div className="history-logs-table__container">
                <table className="history-logs-table__table">
                    <thead>
                        <tr>
                            <th>{t('historyLogTable.headers.action')}</th>
                            <th>{t('historyLogTable.headers.createdAt')}</th>
                            <th>{t('historyLogTable.headers.table')}</th>
                            <th>{t('historyLogTable.headers.primaryId')}</th>
                            <th>{t('historyLogTable.headers.data')}</th>
                            <th>{t('historyLogTable.headers.user')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map((item, index) => {
                            const isExpanded = expandedRows.has(item.id);
                            return (
                                <>
                                    <tr key={item.id} className={`history-logs-table__row ${isExpanded ? 'expanded' : ''}`} onClick={() => toggleRow(item.id)}>
                                        <td>
                                            <span className={`action-badge ${getActionBadgeClass(item.action)}`}>
                                                {item.action}
                                            </span>
                                        </td>
                                        <td>{formatDate(item.created_at)}</td>
                                        <td>
                                            <span className="table-name">{item.table}</span>
                                        </td>
                                        <td>
                                            <span className="primary-id">#{item.primary_id}</span>
                                        </td>
                                        <td>
                                            <div className="data-cell">
                                                {item.data?.title}
                                            </div>
                                        </td>
                                        <td>
                                            {item.user?.name || t('historyLogTable.notAvailable')}
                                        </td>
                                        <td className="history-logs-table__expand-cell">
                                            <button 
                                                className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                                                aria-label={isExpanded ? t('historyLogTable.actions.collapseRow') : t('historyLogTable.actions.expandRow')}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="history-logs-table__expanded-row">
                                            <td colSpan="14">
                                                {renderDetailedData(item, data[index + 1], hiddenKeys, toggleKeyVisibility, allKeys, t)}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Render detailed data view
const renderDetailedData = (item, prevData, hiddenKeys, toggleKeyVisibility, allKeys, t) => {
    if (!item.data || typeof item.data !== 'object') {
        return (
            <div className="detailed-data">
                <div className="detailed-data__empty">
                    <div className="detailed-data__empty-icon">📋</div>
                    <p>{t('historyLogTable.noDetailedData')}</p>
                </div>
            </div>
        );
    }

    // Check if this is an update action and we have previous data
    const isUpdateAction = item.action?.toLowerCase() === 'updated';
    const hasPrevData = prevData && prevData.data && typeof prevData.data === 'object';

    // Filter out hidden keys
    const visibleEntries = Object.entries(item.data).filter(([key]) => !hiddenKeys.has(key));

    return (
        <div className="detailed-data__content">
            <div className="detailed-data__grid">
                {visibleEntries.map(([key, value]) => {
                    // Check if value has changed from previous data
                    const prevValue = hasPrevData ? prevData.data[key] : undefined;
                    const hasChanged = isUpdateAction && hasPrevData && 
                        JSON.stringify(value) !== JSON.stringify(prevValue);
                    
                    return (
                        <div 
                            key={key} 
                            className={`detailed-data__field ${hasChanged ? 'detailed-data__field--changed' : ''}`}
                        >
                            <div className="detailed-data__field-header">
                                <div className="detailed-data__field-label">
                                    <span className="detailed-data__field-name">{key}:</span>
                                </div>
                                <button
                                    className="detailed-data__field-hide-button"
                                    onClick={() => toggleKeyVisibility(key)}
                                    title={`${t('historyLogTable.actions.hideField')} ${key}`}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                        <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="detailed-data__field-value">
                                {value === null ? (
                                    <span className="detailed-data__null-value">{t('historyLogTable.nullValue')}</span>
                                ) : (
                                    <span className="detailed-data__string-value">{String(value)}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HistoryLogsTable;